import React from 'react';

const RENDER_BLOCK = 5;

const RenderBlock = React.memo(({ items, renderItem, block }) => (
  items.slice(block * RENDER_BLOCK, block * RENDER_BLOCK + RENDER_BLOCK).map((item, index) => renderItem(item, index + block * RENDER_BLOCK))
));

const RenderItems = React.memo(({ items, renderItem, firstBlock, lastBlock }) => (
  lastBlock > firstBlock && [...Array(lastBlock - firstBlock)].map((_, i) => <RenderBlock key={i + firstBlock} items={items} renderItem={renderItem} block={i + firstBlock}/>)
));

export function VirtualList({ containerType="ul", itemSelector="li", items, renderItem, maxHeight, itemHeight=18, ...props }) {
  const [firstBlock, setFirstBlock] = React.useState(0);

  const scrollRef = React.useRef();

  const lineHeight = React.useRef(itemHeight);
  React.useEffect(() => {
    const line = scrollRef.current.querySelector(itemSelector);
    if (line) {
      lineHeight.current = line.offsetHeight;
    }
  }, [items, itemSelector]);

  const onScroll = React.useCallback(() => {
    setFirstBlock(Math.max(Math.floor(scrollRef.current.scrollTop / lineHeight.current / RENDER_BLOCK) - 1, 0));
  }, []);

  const lastBlock = Math.min(firstBlock + Math.ceil(maxHeight / lineHeight.current / RENDER_BLOCK) + 4, Math.ceil(items.length / RENDER_BLOCK));
  const renderFirst = firstBlock * RENDER_BLOCK;
  const renderLast = Math.min(renderFirst + (Math.ceil(maxHeight / lineHeight.current / RENDER_BLOCK) + 4) * RENDER_BLOCK, items.length);

  const Container = containerType;

  return (
    <div {...props} ref={scrollRef} onScroll={onScroll}>
      <Container style={{marginTop: renderFirst * lineHeight.current, marginBottom: (items.length - renderLast) * lineHeight.current}}>
        <RenderItems items={items} renderItem={renderItem} firstBlock={firstBlock} lastBlock={lastBlock}/>
      </Container>
    </div>
  );
}

export function VirtualListEx({ containerType="ul", itemSelector="li", items, renderItem, itemHeight=18, containerRect, className }) {
  const [viewportTop, setTop] = React.useState(0);
  const [viewportBottom, setBottom] = React.useState(0);
  const [lineHeight, setLineHeight] = React.useState(itemHeight);

  const scrollRef = React.useRef();
  React.useLayoutEffect(() => {
    const line = scrollRef.current.querySelector(itemSelector);
    if (line) setLineHeight(line.offsetHeight);
  }, [items, itemSelector]);

  React.useLayoutEffect(() => {
    if (!scrollRef.current || !containerRect) return;
    const innerRect = scrollRef.current.getBoundingClientRect();
    const top = -(innerRect.top - containerRect.top);
    setTop(top);
    setBottom(top + containerRect.height);
  }, [containerRect, items]);

  const firstItem = Math.max(0, Math.min(Math.floor(viewportTop / lineHeight), items.length - 1));
  const lastItem = Math.max(0, Math.min(Math.ceil((viewportBottom + Math.max(0, -viewportTop)) / lineHeight), items.length));
  const firstBlock = Math.floor(firstItem / RENDER_BLOCK) - 1;
  const lastBlock = Math.ceil(lastItem / RENDER_BLOCK) + 1;
  const renderFirst = Math.max(0, firstBlock * RENDER_BLOCK);
  const renderLast = Math.min(lastBlock * RENDER_BLOCK, items.length);

  const Container = containerType;

  return (
    <Container ref={scrollRef} className={className} style={{paddingTop: renderFirst * lineHeight, paddingBottom: (items.length - renderLast) * lineHeight}}>
      <RenderItems items={items} renderItem={renderItem} firstBlock={firstBlock} lastBlock={lastBlock}/>
    </Container>
  );
}
